import { getBlockComponent } from "@/common/blocks/registry";
import type { ChartContent } from "@/types/report";

interface Props {
  content: ChartContent;
}

/**
 * Normalize LLM-generated chart data to the format our Recharts components expect.
 * LLMs often return Chart.js-style data (labels + datasets) instead of Recharts-style (items).
 */
function normalizeChartData(blockType: string, data: any): any {
  if (!data) return data;

  // Bar chart: Chart.js { labels, datasets } -> Recharts { items: [{ label, value }] }
  if (blockType === 'bar-chart' && data.datasets && data.labels && !data.items) {
    return {
      items: data.labels.map((label: string, i: number) => ({
        label,
        value: data.datasets[0]?.data?.[i] ?? 0,
      })),
    };
  }

  // Line/area chart: Chart.js { labels, datasets } -> Recharts { series: [{ date, value }] }
  if ((blockType === 'line-chart' || blockType === 'area-chart') && data.datasets && data.labels && !data.series) {
    return {
      series: data.labels.map((label: string, i: number) => ({
        date: label,
        value: data.datasets[0]?.data?.[i] ?? 0,
      })),
    };
  }

  // Pie/donut chart: array of { label, data } -> { segments: [{ label, value }] }
  if ((blockType === 'pie-chart' || blockType === 'donut-chart') && data.datasets && data.labels && !data.segments) {
    return {
      segments: data.labels.map((label: string, i: number) => ({
        label,
        value: data.datasets[0]?.data?.[i] ?? 0,
      })),
    };
  }

  return data;
}

export function ChartSection({ content }: Props) {
  const Component = getBlockComponent(content.blockType);

  if (!Component) {
    return (
      <div className="report-chart-section">
        <p>Unsupported chart type: {content.blockType}</p>
      </div>
    );
  }

  const normalizedData = normalizeChartData(content.blockType, content.blockData);

  return (
    <div className="report-chart-section">
      <Component data={normalizedData} />
      {content.caption && <div className="caption">{content.caption}</div>}
    </div>
  );
}
